export interface StartInteractiveFictionSessionRequest {
  title?: string;
  concept?: string;
  genre?: string;
  themeAndMood?: string;
  storyFormat?: string;
  lengthInPassages?: number;
  playerCharacterDescription?: string;
  settingDescription?: string;
  targetAudience?: string;
  additionalInstructions?: string;
  maxTokensPerTurn?: number;
}

export interface InteractiveFictionSessionStartResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  openingPassage?: InteractiveStoryPassage;
}

export interface InteractiveStoryPassage {
  id?: string;
  title?: string;
  content?: string;
  isOpeningPassage: boolean;
  isEndingPassage: boolean;
  choices?: PassageChoice[];
  metadata?: { [key: string]: string };
  createdAt?: string;
  gameMechanics?: string[];
  parentPassageId?: string;
  passageIndex?: number;
}

export interface PassageChoice {
  id?: string;
  text?: string;
  targetPassageId?: string;
  condition?: string;
  consequence?: string;
}

export interface CreateBranchPassageRequest {
  sourcePassageId?: string;
  choiceText?: string;
  title?: string;
  branchDirection?: string;
  storyBeatType?: string;
  isEndingPassage?: boolean;
  customContent?: string;
  additionalInstructions?: string;
  maxTokens?: number;
}

export interface OptimizeEngagementRequest {
  passageId?: string;
  engagementFocus?: string;
  targetEmotions?: string;
  additionalInstructions?: string;
  maxTokens?: number;
}

export interface AddGameMechanicsRequest {
  passageId?: string;
  mechanicsToAdd?: string[];
  gameStateChanges?: { [key: string]: string };
  additionalInstructions?: string;
  maxTokens?: number;
}

export interface InteractiveFictionPassageResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  passage?: InteractiveStoryPassage;
  previousContent?: string;
}

export interface InteractiveFictionSessionStatusResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  sessionStatus?: string;
  allPassages?: InteractiveStoryPassage[];
  createdAt?: string;
  lastUpdated?: string;
  totalPassages?: number;
}

export interface InteractiveFictionVisualizationResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  visualization?: string;
  passageCount?: number;
  branchingFactor?: number;
}

// Local storage models
export interface InteractiveFictionSession {
  id: string;
  sessionId?: string;
  title: string;
  concept?: string;
  genre?: string;
  passages: InteractiveStoryPassage[];
  currentPassageId?: string;
  createdAt: Date;
}

export interface InteractiveFictionSessionStorage {
  sessions: InteractiveFictionSession[];
  lastUpdated: Date;
}

// Constants
export const INTERACTIVE_FICTION_GENRES = [
  'Fantasy',
  'Science Fiction',
  'Mystery',
  'Horror',
  'Romance',
  'Adventure',
  'Historical Fiction',
  'Thriller',
  'Comedy',
  'Drama',
  'Slice of Life',
  'Dystopian',
  'Cyberpunk',
  'Steampunk',
  'Urban Fantasy'
];

export const STORY_FORMATS = [
  'Choose Your Own Adventure',
  'Parser-Based',
  'Visual Novel',
  'Hypertext Fiction',
  'Gamebook',
  'Interactive Drama'
];

export const STORY_BEAT_TYPES = [
  'Exposition',
  'Rising Action',
  'Climax',
  'Falling Action',
  'Resolution',
  'Plot Twist',
  'Character Development',
  'World Building',
  'Conflict Introduction',
  'Cliffhanger'
];

export const ENGAGEMENT_FOCUS_OPTIONS = [
  'Emotional Impact',
  'Suspense',
  'Mystery',
  'Action',
  'Humor',
  'Drama',
  'Pacing',
  'Character Connection',
  'World Immersion',
  'Choice Consequence'
];

export const GAME_MECHANICS = [
  'Inventory System',
  'Character Stats',
  'Relationship Tracking',
  'Time Limit',
  'Resource Management',
  'Skill Checks',
  'Moral Alignment',
  'Health/Energy',
  'Quest Log',
  'Achievement System'
];